// Bundle-safe side-channel for OG primitives to register image URLs
// they're about to render. Lives in the primitives directory because
// `<OgImage>` and `<OgAvatar>` are imported by client components (for
// the in-app share-modal preview), and the underlying tracker uses
// `node:async_hooks` which cannot be bundled for the browser.
//
// Resolution: this shim reads a function off `globalThis` set by
// `renderOg()` (server-only) before each Satori render. When called
// outside a render scope (client preview, workspace), the function is
// undefined and registration is a no-op. No `node:` imports here, so
// safe to bundle anywhere.

const REGISTER_KEY = '__morfi_og_image_register__' as const;

interface RegisterHost {
  [REGISTER_KEY]?: (url: string) => void;
}

/** Server-side: bind the tracker's `add` function so primitives below
 *  can register URLs they intend to render. Called by `renderOg()` */
export function setImageUrlRegister(register: ((url: string) => void) | null): void {
  const host = globalThis as RegisterHost;
  if (register === null) {
    delete host[REGISTER_KEY];
    return;
  }
  host[REGISTER_KEY] = register;
}

/** Primitive-side: report an absolute image URL to the active tracker
 *  if any. No-op outside a render scope (client preview, workspace,
 *  tests). Filters relative URLs and data URIs — Satori only fetches
 *  http(s) URLs. */
export function registerImageUrl(url: string | null | undefined): void {
  if (typeof url !== 'string' || url.length === 0) return;
  if (url.startsWith('data:')) return;
  if (!url.startsWith('http://') && !url.startsWith('https://')) return;
  const host = globalThis as RegisterHost;
  const register = host[REGISTER_KEY];
  if (register === undefined) return;
  register(url);
}
